import dayjs from 'dayjs';
import { find, round } from 'lodash';
import { Alert, DialogPlugin, Link, Space, Tag } from 'tdesign-react';
import qs from 'qs';
import { GestureRightIcon } from 'tdesign-icons-react';
import BadgeStatus from '@/components/BadgeStatus';
import { getItem, setItem } from './storage';

export const amountUnits = ['', '万', '亿', '兆', '京', '垓'];
export const amountMultiples = [1, 10000, 10000, 10000, 10000, 10000];

export const byteUnits = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
export const byteMultiples = [1, 1024, 1024, 1024, 1024, 1024];

export const timeUnits = ['ms', 's', 'm', 'h'];
export const timeMultiples = [1, 1000, 60, 60];

/**
 * @typedef {Object} Transfer
 * @property {number} value 转换后的值，四舍五入两位小数
 * @property {number} originValue 转换前的值
 * @property {string} unit 单位
 * @property {number} multiplier 倍数
 * @property {string} pretty 美化后的字符串，带单位
 */

/**
 * 单位转换
 * @param {number} num 要转换的数字
 * @param {Array<string>} units 单位
 * @param {Array<number>} multiples 倍数
 *
 * @returns {Transfer}
 *
 */
export const convert = (num, units = [], multiples = []) => {
  let unit; // 单位
  let multiplier = 1; // 总换算比例

  for (let index = 0; index < units.length; index++) {
    unit = units[index];
    multiplier *= multiples[index]; // 总转换比例
    if (num < multiplier * multiples[index + 1]) {
      // 如果没达到下一级，结束
      break;
    }
  }

  const transferNum = round(num / multiplier, 2);

  return {
    value: transferNum,
    originValue: num,
    unit,
    multiplier,
    pretty: `${transferNum}${unit ? ` ${unit}` : ''}`,
  };
};

// 格式化数量
export const convertAmount = (num) => {
  return convert(num, amountUnits, amountMultiples);
};

// 字节转换
export const convertByte = (num) => {
  return convert(num, byteUnits, byteMultiples);
};

// 时间转换
export const convertTime = (num) => {
  return convert(num, timeUnits, timeMultiples);
};

// 时间转换 从秒开始
export const convertTimeFromSecond = (num) => {
  return convert(num, timeUnits.slice(1), timeMultiples);
};

// 空判断
export function isEmpty(val) {
  if (
    val === null ||
    typeof val === 'undefined' ||
    (typeof val === 'string' && val === '' && val !== 'undefined')
  ) {
    return true;
  }
  return false;
}

// 别名
export const isVoid = isEmpty;

/**
 * 根据提供的行数据和列配置，渲染枚举值。
 *
 * @param {Object} options - 选项对象。
 * @param {Object} options.row - 行数据。
 * @param {Object} options.col - 列配置。
 * @param {Object} options.col.valueEnum - 枚举值对象，包含枚举项和对应的值。
 * @param {string} options.col.colKey - 用于从行数据中获取列值的键。
 * @returns {string|undefined} - 返回枚举值对应的显示文本，如果没有找到则返回 undefined。
 */
export const renderEnum = ({ row, col }) => {
  const enumObj = col.valueEnum;
  const itemKey = row[col.colKey];
  return enumObj[itemKey];
};

/**
 * 根据options进行渲染
 * @returns
 */
export const renderOptions = ({ row, col }) => {
  const { options } = col;
  const item = row[col.colKey];
  return find(options, { value: item })?.label || '-';
};

/**
 * 根据options进行渲染,带状态效果
 * @returns
 */
export const renderStatusOptions = ({ row, col }) => {
  const { options } = col;
  const item = row[col.colKey];
  const found = find(options, { value: item });
  return <BadgeStatus {...found} />;
};

// 日期渲染
export function renderDateTime({ row, col }) {
  const item = row[col.colKey];
  if (isEmpty(item)) {
    return '-';
  }
  return dayjs(item).format('YYYY-MM-DD HH:mm:ss');
}

// 毫秒日期渲染
export function renderMillDateTime({ row, col }) {
  const item = row[col.colKey];
  if (isEmpty(item)) {
    return '-';
  }
  return dayjs(item).format('YYYY-MM-DD HH:mm:ss.SSS');
}

export const renderNum = ({ row, col }) => {
  const item = row[col.colKey];
  if (isEmpty(item)) {
    return '-';
  }
  const { pretty } = convertAmount(item);
  return pretty;
};

// 数字速率转换
export const renderNumRate = (params) => `${renderNum(params)}/s`;

// 字节
export const renderByte = ({ row, col }) => {
  const item = row[col.colKey];
  if (isEmpty(item)) {
    return '-';
  }
  const { pretty } = convertByte(item);
  return pretty;
};

// 字节速率
export const renderByteRate = (params) => `${renderByte(params)}/s`;

// 渲染时间单位
export const renderTimeUnit = ({ row, col }) => {
  const item = row[col.colKey];
  if (isEmpty(item)) {
    return '-';
  }
  const { pretty } = convertTime(item);
  return pretty;
};

// 渲染时间单位，从秒开始
export const renderTimeUnitFromSecond = ({ row, col }) => {
  const item = row[col.colKey];
  if (isEmpty(item)) {
    return '-';
  }
  const { pretty } = convertTimeFromSecond(item);
  return pretty;
};

// 渲染标签,包含逗号分隔或者数组的情况
export const renderTags = ({ row, col }) => {
  let item = row[col.colKey];

  item = isVoid(item) ? '' : item;

  const itemsArray = Array.isArray(item) ? item : String(item).split(',');

  if (isVoid(item) || itemsArray.length === 0) {
    return '-';
  }

  return (
    <Space breakLine size='small'>
      {itemsArray.map((item, index) => (
        <Tag key={index}>{item}</Tag>
      ))}
    </Space>
  );
};

// 获取一级路径
export function getRootPath(path) {
  return path.replace(/(^\/[a-zA-Z-]+)(.*)/, '$1');
}

/**
 * 下载文件函数，将 Blob 对象保存为指定的文件名。
 * @param {Blob} blob - 要下载的 Blob 对象。
 * @param {string} fileName - 下载文件的名称。
 * @returns {void}
 */
export const downloadFile = (blob, fileName) => {
  // 创建 <a> 元素
  const link = document.createElement('a');

  // 设置下载文件的属性
  link.download = fileName;
  link.href = URL.createObjectURL(blob);

  // 将 <a> 元素隐藏并添加到 DOM
  link.style.display = 'none';
  document.body.appendChild(link);

  // 触发点击事件以下载文件
  link.click();

  // 清理资源并移除 <a> 元素
  URL.revokeObjectURL(link.href);
  document.body.removeChild(link);
};

// await sleep
export const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * 检查字符串是否为合法，且不为空的 JSON 字符串
 * @param {string} str - 需要检查的字符串.
 * @returns {boolean} - 如果字符串是合法的 JSON 字符串，且不为空或者bool等基础类型，返回 true，否则返回 false.
 */
export function isNotEmptyJSONStr(str) {
  if (isVoid(str)) return false;
  try {
    const result = JSON.parse(str);
    if (typeof result === 'object') {
      return true;
    }
    return false;
  } catch (e) {
    return false;
  }
}

// 简单校验JSON，如有值，校验是否合法
export function isValidJSON(str) {
  if (!str) return true;
  try {
    const result = JSON.parse(str);
    if (typeof result === 'object') {
      return true;
    }
    return false;
  } catch (e) {
    return false;
  }
}

// 简单校验JSON，如有值，校验是否合法 RCForm专用
export function isValidJSONRC(_, str) {
  if (!str) return Promise.resolve();
  try {
    const result = JSON.parse(str);
    if (typeof result === 'object') {
      return Promise.resolve();
    }
    return Promise.reject('存在非法JSON');
  } catch (e) {
    return Promise.reject('存在非法JSON');
  }
}

// 简单校验JSON，如有值，校验是否合法 RCForm专用 不允许嵌套结构
export function isValidSimpleJSONRC(_, str) {
  if (!str) return Promise.resolve();
  let isNested = false;
  try {
    const result = JSON.parse(str);
    if (typeof result === 'object') {
      for (const [, value] of Object.entries(result)) {
        if (typeof value === 'object') {
          isNested = true;
          break;
        }
      }
      if (isNested) return Promise.reject('存在嵌套JSON');
      return Promise.resolve();
    }
    return Promise.reject('存在非法JSON');
  } catch (e) {
    return Promise.reject('存在非法JSON');
  }
}

// 简单校验JSON，如有值，校验是否合法 RCForm专用 不允许嵌套结构 不允许空值
export function isValidSimpleValueRequiredJSONRC(_, str) {
  if (!str) return Promise.resolve();
  let isNested = false;
  let hasEmptyValue = false; // 存在空值
  try {
    const result = JSON.parse(str);
    if (typeof result === 'object') {
      for (const [, value] of Object.entries(result)) {
        if (typeof value === 'object') {
          isNested = true;
          break;
        }
        if (isVoid(value)) {
          hasEmptyValue = true;
          break;
        }
      }
      if (isNested) return Promise.reject('存在嵌套JSON');
      if (hasEmptyValue) return Promise.reject('存在空值');
      return Promise.resolve();
    }
    return Promise.reject('存在非法JSON');
  } catch (e) {
    return Promise.reject('存在非法JSON');
  }
}

/**
 * Tdesign bug临时方案
 * FormList 必须手工再赋值处理
 * @deprecated 仅兼容历史代码, 复杂表单请使用 \@/components/Form
 *  */
export const UNSAFE_bugSetFieldsValue = async (form, value, tryTimes = 4) => {
  try {
    for (let index = 0; index < tryTimes; index++) {
      form.setFieldsValue(value);
      await sleep(20);
    }
  } catch (error) {
    console.trace('UNSAFE_bugSetFieldsValue', error);
  }
};

// 仅在 ascentstream.com 和 localhost 上展示
export const isAscentstream = () => {
  const domain = window.location.hostname;
  return domain.includes('ascentstream.com') || domain.includes('localhost');
};

export const changeFavico = (ico) => {
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = ico;
};

// 随机7位数字和字母，不区分大小写
export const randomString = (len = 7) => {
  const $chars = 'ABCDEFGHJKMNPQRSTWXYZabcdefhijkmnprstwxyz2345678';
  const maxPos = $chars.length;
  let pwd = '';
  for (let i = 0; i < len; i++) {
    pwd += $chars.charAt(Math.floor(Math.random() * maxPos));
  }
  return pwd;
};

/**
 * 处理td本身问题，默认行为超过100条数据会开启虚拟滚动。但label value结构会报错
 * 因此对内置的 options 转换。 临时处理方案：将 label 置入 content
 * @param {*} options
 * @returns
 */
export const toTsOptions = (options) => {
  return options?.map((item) => {
    return {
      ...item,
      content: isVoid(item.content) ? item.label : item.content,
    };
  });
};

/**
 * url路径拆分为对象，protocol、host、port、path
 * @param {string} url
 * @returns
 */
export const parseUrl = (url) => {
  const { protocol, hostname, port, pathname } = new URL(url);
  return {
    protocol,
    host: hostname,
    port,
    path: pathname,
  };
};

// 处理70101 meta code
// 主集群创建的资源已在灾备集群存在
export const deal70101 = (publicMessage, onRetry, urlParams = {}) => {
  const { url, ...others } = urlParams;
  const [clusterName, resource = '', errorMsg] = publicMessage
    .matchAll(/\{(.*?)\}/g)
    .map((item) => item[1]);
  const urlParamsStr = qs.stringify({ ...others, name: clusterName }, { addQueryPrefix: true });
  const dialog = DialogPlugin.confirm({
    theme: 'warning',
    header: (
      <Space size={'small'} breakLine align='center'>
        备集群操作失败
        {resource.split(',').map((item, index) => (
          <Tag key={index}>{item}</Tag>
        ))}
      </Space>
    ),
    width: 700,
    confirmBtn: {
      content: '关闭并重试',
    },
    body: (
      <>
        <b>{errorMsg}</b>

        <Alert
          theme='info'
          style={{ marginTop: 10 }}
          message='请前往备集群查看并处理'
          operation={
            <Link theme='primary' target='_blank' href={`${url}${urlParamsStr}`}>
              <Space size={2}>
                <GestureRightIcon /> {clusterName}
              </Space>
            </Link>
          }
        />
      </>
    ),
    onClose: () => {
      dialog.destroy();
    },
    onConfirm: () => {
      dialog.destroy();
      if (onRetry) {
        onRetry();
      }
    },
  });
};

// 一次性消息提示相关

// 检查消息是否不需要显示
export const shouldShowMessage = (messageType) => {
  const isHidesInfoTypes = getItem('isHidesInfoTypes') || [];
  return !isHidesInfoTypes.includes(messageType);
};

// 设置消息不再提示
export const hideShowMessage = (messageType) => {
  const isHidesInfoTypes = getItem('isHidesInfoTypes') || [];
  setItem('isHidesInfoTypes', Array.from(new Set(isHidesInfoTypes).add(messageType)));
};
